import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import { resetPasswordApi, sendOtpApi, verifyOtpApi } from "../../services/authService";

type Step = 1 | 2 | 3;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");


  /* STEP 1: SEND OTP */
const handleSendOtp = async () => {
  if (!email) {
    toast.error("Please enter email");
    return;
  }

  setLoading(true);
  try {
    const res = await sendOtpApi({ email });

    if (res.data?.status) {
      toast.success(res.data.message);
      setStep(2);
    } else {
      toast.error(res.data?.message);
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to send OTP");
  } finally {
    setLoading(false);
  }
};


  /* STEP 2: VERIFY OTP */
const handleVerifyOtp = async () => {
  if (!otp) {
    toast.error("Enter OTP");
    return;
  }

  setLoading(true);
  try {
    const res = await verifyOtpApi({ email, otp });

    if (res.data?.status) {
      setResetToken(res.data.reset_token);
      toast.success(res.data.message);
      setStep(3);
    } else {
      toast.error(res.data?.message);
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Invalid OTP");
  } finally {
    setLoading(false);
  }
};


  /* STEP 3: RESET PASSWORD */
const handleResetPassword = async () => {
  if (!password || !confirmPassword) {
    toast.error("Fill all fields");
    return;
  }

  if (password !== confirmPassword) {
    toast.error("Passwords do not match");
    return;
  }

  setLoading(true);
  try {
    const res = await resetPasswordApi({
      reset_token: resetToken,
      password,
      password_confirmation: confirmPassword,
    });

    if (res.data?.status) {
      toast.success(res.data.message);
      navigate("/login", { replace: true });
    } else {
      toast.error(res.data?.message);
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to reset password");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-xl w-full max-w-md shadow-lg">
        <h2 className="text-2xl font-semibold text-center mb-4">
          Forgot Password
        </h2>

        {/* STEP 1 */}
        {step === 1 && (
          <>
            <label className="block">
              <span>Email</span>
              <InputField
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                // className="mt-1 w-full border rounded-md px-3 py-2"
              />
            </label>

            <Button
              onClick={handleSendOtp}
              disabled={loading}
              className="mt-4 w-full "
            >
              {loading ? "Sending..." : "Send OTP"}
            </Button>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <>
            <label className="block">
              <span>Enter OTP</span>
              <InputField
              placeholder="Enter OTP"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                // className="mt-1 w-full border rounded-md px-3 py-2"
              />
            </label>

            <Button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="mt-4 w-full "
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </Button>
          </>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <>
            <label className="block">
              <span>New Password</span>
              <InputField
               placeholder="Enter Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 "
              />
            </label>

            <label className="block mt-3">
              <span>Confirm Password</span>
              <InputField
                placeholder="Enter Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 "
              />
            </label>

            <Button
              onClick={handleResetPassword}
              disabled={loading}
              className="mt-4 w-full"
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </>
        )}

        {/* Back to Login */}
        <p
          onClick={() => navigate("/login")}
          className="text-center text-sm mt-4 text-yellow-500 cursor-pointer"
        >
          Back to Login
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
